package com.vidaensupalabra.vsp.ventanas

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

import com.vidaensupalabra.vsp.datos.ArdeRepository
import com.vidaensupalabra.vsp.ui.theme.VspBase
import com.vidaensupalabra.vsp.ui.theme.VspMarco
import com.vidaensupalabra.vsp.ui.theme.VspMarcoTransparente50
import com.vidaensupalabra.vsp.ui.theme.White
import com.vidaensupalabra.vsp.utils.*

@Composable
fun ARDEScreen(onNavigateToDetail: (Int, Int, Int) -> Unit) {
    LaunchedEffect(Unit) {
        ArdeRepository.loadDataIfNeeded()
    }
    
    ArdeContent(onNavigateToDetail = onNavigateToDetail)
}

@Composable
private fun ArdeContent(onNavigateToDetail: (Int, Int, Int) -> Unit) {
    var fontSize by remember { mutableStateOf(getFromLocalStorage("arde_intro_font_size")?.toIntOrNull() ?: 16) }
    val onChangeFontSize: (Int) -> Unit = { delta ->
        val newSize = (fontSize + delta).coerceIn(12, 40)
        if (newSize != fontSize) {
            fontSize = newSize
            saveToLocalStorage("arde_intro_font_size", newSize.toString())
        }
    }

    // Contenedor principal que reemplaza a Scaffold
    Div(attrs = {
        style {
            width(100.percent)
            height(100.percent) // Asegura que el contenedor no sea más alto que la pantalla
            backgroundColor(VspBase)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            fontFamily("sans-serif")
            boxSizing("border-box")
            padding(16.px)
        }
    }) {
        MethodArdeDescriptionCard(fontSize = fontSize, onChangeFontSize = onChangeFontSize)

        H2(attrs = {
            style {
                color(White)
                textAlign("center")
                marginTop(24.px)
                marginBottom(16.px)
                fontSize(20.px)
            }
        }) {
            Text("Seleccione una fecha para ver el ARDE.")
        }
        
        YearSelectionButtons(onYearSelected = onNavigateToDetail)
    }
}

@Composable
private fun MethodArdeDescriptionCard(fontSize: Int, onChangeFontSize: (Int) -> Unit) {
    // Tarjeta principal
    Div(attrs = {
        style {
            width(100.percent)
            maxWidth(800.px)
            flex(1) // Hace que la tarjeta tome todo el espacio disponible
            property("overflow-y", "auto") // Permite hacer scroll solo dentro de la tarjeta
            backgroundColor(rgba(255, 255, 255, 0.05)) // Ligero contraste con VspBase
            borderRadius(12.px)
            padding(24.px)
            property("box-shadow", "0px 4px 8px rgba(0,0,0,0.2)")
            boxSizing("border-box")
        }
    }) {
        Div(attrs = { style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center); marginBottom(16.px) } }) {
            H1(attrs = {
                style { color(White); fontSize(28.px); margin(0.px) }
            }) { Text("Información sobre A.R.D.E") }
            
            Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center) } }) {
                Button(attrs = { onClick { onChangeFontSize(-2) }; style { backgroundColor(Color("transparent")); color(White); border(0.px); fontWeight("bold"); fontSize(14.px); cursor("pointer") } }) { Text("A-") }
                Button(attrs = { onClick { onChangeFontSize(2) }; style { backgroundColor(Color("transparent")); color(White); border(0.px); fontWeight("bold"); fontSize(18.px); cursor("pointer") } }) { Text("A+") }
            }
        }
        
        // Reemplazo de buildAnnotatedString por HTML semántico puro
        Div(attrs = {
            style { color(White); fontSize(fontSize.px); lineHeight("1.6") }
        }) {
            P {
                Text("El ")
                B { Text("método ARDE") }
                Text(" es una herramienta práctica para tu tiempo devocional diario (tu tiempo a solas con Dios). Su objetivo no es que leas la Biblia de forma mecánica o por obligación, sino que la entiendas y tu corazón sea transformado por ella.")
            }
            P {
                Text("Para empezar, la recomendación es alejar distracciones: apaga el celular, usa una Biblia física, toma un cuaderno y ora pidiéndole al Espíritu Santo que te guíe y te ayude a entender.")
            }
            P {
                Text("Una vez que lees el capítulo del día, el método se divide en un paso inicial y cuatro letras:")
            }
            Ul {
                Li {
                    B { Text("El paso inicial: ¿Qué dice el texto? ") }
                    Text("Antes de ir a las siglas, debes resumir de qué trata lo que acabas de leer. Trata de escribir la idea principal en una sola frase corta y sencilla. Esto te ayuda a no irte por las ramas y mantener el enfoque.")
                }
            }
            P {
                Text("Ahora sí, respondes en tu cuaderno a las 4 letras de la palabra ARDE:")
            }
            Ul {
                Li {
                    B { Text("A - Atributos de Dios (¿Qué dice el texto sobre Él?) ") }
                    Text("Pregúntate cómo se muestra Dios en ese pasaje. Busca sus nombres (ej. el Señor, el creador), sus características (ej. es justo, santo, amoroso) o sus acciones (ej. Él perdona, Él castiga, Él provee). La idea no es hacer una lista aburrida, sino meditar en quién es Dios para asombrarte y adorarlo.")
                }
                Br()
                Li {
                    B { Text("R - Relación con Cristo y el Evangelio (¿Cómo se conecta con Jesús?) ") }
                    Text("Este es el corazón del método. Toda la Biblia trata sobre Jesús, desde Génesis hasta Apocalipsis. Pregúntate cómo la historia que leíste apunta a Él. A veces verás promesas directas, otras veces verás el fracaso y el pecado del ser humano, lo cual nos recuerda cuánto necesitamos a un Salvador que nos perdone y nos rescate.")
                }
                Br()
                Li {
                    B { Text("D - Deberes (¿Qué me pide Dios que haga?) ") }
                    Text("A la luz de lo que Cristo ya hizo por ti, pregúntate qué te exige el texto. A veces verás mandamientos directos (como \"no mientas\" o \"ama a tu prójimo\"). Otras veces, verás historias de personajes: fíjate en sus buenas conductas para imitarlos, o en sus malos ejemplos para huir de esos pecados.")
                }
                Br()
                Li {
                    B { Text("E - Esperanza (¿Qué consuelo me da esto hoy?) ") }
                    Text("Todo estudio de la Biblia debe terminar dándote paz y paciencia para enfrentar las luchas de este mundo. Busca qué promesas directas hace Dios en el texto, o qué realidades favorables tiene para sus hijos (por ejemplo, saber que Él es tu pastor o que Él escucha tu clamor). Esa esperanza es tu combustible para el día.")
                }
            }
            P {
                B { Text("Un último consejo: ") }
                Text("Haz este ejercicio tú solo con la Biblia. No vayas primero a comentarios en internet, a sermones de pastores o a inteligencias artificiales. Deja que el Espíritu Santo te hable primero directamente a través de Su Palabra.")
            }
        }
    }
}

@Composable
private fun YearSelectionButtons(onYearSelected: (Int, Int, Int) -> Unit) {
    var showDialogForYear by remember { mutableStateOf<Int?>(null) }

    Div(attrs = {
        style {
            width(100.percent)
            maxWidth(600.px)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceEvenly)
            flexWrap(FlexWrap.Wrap)
            gap(16.px)
        }
    }) {
        listOf(1, 2, 3).forEach { planYear ->
            Button(attrs = {
                onClick { showDialogForYear = planYear }
                style {
                    padding(12.px, 32.px)
                    backgroundColor(VspMarcoTransparente50)
                    color(White)
                    fontSize(16.px)
                    fontWeight("bold")
                    border(0.px)
                    borderRadius(8.px)
                    cursor("pointer")
                }
            }) { Text("Año $planYear") }
        }
    }
    
    showDialogForYear?.let { year ->
        MonthDaySelectorDialog(
            planYear = year,
            onDismiss = { showDialogForYear = null },
            onDateSelected = { y, m, d -> 
                showDialogForYear = null
                onYearSelected(y, m, d) 
            }
        )
    }
}

@Composable
private fun MonthDaySelectorDialog(planYear: Int, onDismiss: () -> Unit, onDateSelected: (Int, Int, Int) -> Unit) {
    // Obtenemos la fecha actual para pre-poblar el Input nativo
    val today = remember { Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()) }
    val todayStr = "${today.year}-${today.monthNumber.toString().padStart(2, '0')}-${today.dayOfMonth.toString().padStart(2, '0')}"
    var selectedDateStr by remember { mutableStateOf(todayStr) }

    // Overlay Modal
    Div(attrs = {
        style { position(Position.Fixed); top(0.px); left(0.px); width(100.vw); height(100.vh)
            backgroundColor(rgba(0, 0, 0, 0.7)); display(DisplayStyle.Flex); justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center); property("z-index", 1000) }
    }) {
        // Contenedor del Diálogo
        Div(attrs = {
            style { backgroundColor(VspBase); padding(24.px); borderRadius(16.px); display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column); alignItems(AlignItems.Center); minWidth(280.px) }
        }) {
            H3(attrs = { style { color(White); marginTop(0.px); marginBottom(24.px) } }) { Text("Seleccionar Fecha") }
            
            // Magia del DOM: Input nativo de tipo date, adiós al DatePicker de Material 3
            Input(type = InputType.Date, attrs = {
                value(selectedDateStr)
                onInput { selectedDateStr = it.value }
                style { padding(12.px); borderRadius(8.px); border(1.px, LineStyle.Solid, VspMarco); fontSize(16.px); marginBottom(24.px) }
            })

            Div(attrs = { style { display(DisplayStyle.Flex); gap(16.px); width(100.percent); justifyContent(JustifyContent.End) } }) {
                Button(attrs = { onClick { onDismiss() }; style { backgroundColor(Color.transparent); color(Color.gray); border(0.px); cursor("pointer"); fontSize(16.px) } }) { Text("Cancelar") }
                Button(attrs = { 
                    onClick {
                        val parts = selectedDateStr.split(Regex("[-/]"))
                        val m = parts.getOrNull(1)?.toIntOrNull() ?: today.monthNumber
                        val d = parts.getOrNull(2)?.toIntOrNull() ?: today.dayOfMonth
                        onDateSelected(planYear, m, d)
                    }
                    style { backgroundColor(VspMarco); color(White); border(0.px); padding(8.px, 16.px); borderRadius(8.px); cursor("pointer"); fontSize(16.px); fontWeight("bold") } 
                }) { Text("Aceptar") }
            }
        }
    }
}